import React, {useMemo, useState} from 'react';
import {TText, TRecord, Expression, EText} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  EText as aEText,
  getAnn,
  stripAll,
  stripAnn,
  updateExpr,
} from '@packages/utilities/contextual-expression';
import {
  extendExpressionEditorActionAtPath,
  ExpressionEditor,
  getBindingLabel,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {CustomControls} from '@packages/systems/ExpressionEditor-custom-controls';
import {
  FieldTextIcon,
  HStack,
  Note,
  Tag,
  Text as SpringText,
} from '@packages/systems/spring';
import {Text} from '@packages/systems/design-system/Text';
import {
  TypeImplementation,
  Expressions,
  getMeta,
  withSystem,
  type ExpressionPath,
  TypeCheckerState,
  getEnvironment,
  matchExpression,
  VariableMap,
  EnvTypes,
} from '@packages/systems/wfdl';
import {emotionStyled} from '@packages/utilities/styled';
import {hasValue} from '@packages/utilities/string-utils';
import {
  isInternalAttr,
  isValidAttrName,
  isValidAttrValue,
  isReservedAttr,
} from '@packages/systems/core/utils/HtmlUtils';
import {selectSelectedElement} from '@packages/utilities/platform-selectors';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectSiteId} from '@packages/systems/designer/site-data';
import {trackEvent} from '@packages/utilities/analytics';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {parseAttributePaste} from './parseAttributePaste';

// Cap on the bound-item summary row so a long side can never push past the
// sidebar's right edge. 56px reserves sidebar padding, item padding, and the
// row-action wrapper on the right.
const ATTRIBUTE_SUMMARY_MAX_WIDTH = rightSidebarWidth - 56;

// Each cell competes for the row width from a zero basis, but can't grow past
// its content's natural width. When one side is shorter than half, it freezes
// at its content size and the flex algorithm redistributes the surplus to the
// other cell. When both exceed half, neither freezes and each stays at 50%
// (truncating with `…`). `min-width: 0` allows shrinking below flex's default
// min-content floor.
const AttributeKeyCell = emotionStyled('span')({
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: 'max-content',
  overflow: 'hidden',
});

type ValueInputStatus = 'invalid' | 'valid';

type NameInputStatus =
  | 'valid'
  | 'invalidLength'
  | 'invalidCharacters'
  | 'taken'
  | 'reserved';

const Container = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const styledNote = {
  width: '100%',
} as const;

const nameErrorText = {
  taken: 'This attribute already exists.',
  reserved: 'This is a reserved name.',
  invalidLength: 'The name field must be at least two characters',
  invalidCharacters: 'Contains invalid characters',
} as const;
const NAME_PATH: ExpressionPath = [{in: 'Record', at: 'name'}];

const VALUE_PATH: ExpressionPath = [{in: 'Record', at: 'value'}];

export const XATTR_PATH: ExpressionPath = [{in: 'Record', at: 'xattr'}];
// Custom Element / HTML Embed (`Builtin::DOM` / `Builtin::DangerousDOM`) store
// the list at `data.attributes` instead of `data.xattr`. The duplicate-name
// check below tries both slots.
const ATTRIBUTES_PATH: ExpressionPath = [{in: 'Record', at: 'attributes'}];

const VALUE_ERROR_MESSAGE = 'Cannot contain double quotes.';

const constantEmptyArray = (_: Expression) => [];
const constantEmptyString = (_: Expression) => '';

const getCustomAttributesList = matchExpression({
  List: (list) =>
    list.map(
      matchExpression({
        // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
        Record: ({name}) => String(Expressions.getText(name) || ''),
        _: constantEmptyString,
      })
    ),
  _: constantEmptyArray,
});

const isNameTooShort = (input: string): boolean => {
  return !hasValue(input) || input.length < 2;
};
// `isValidAttrName` from HtmlUtils requires `[a-zA-Z_][-a-zA-Z0-9_:]+` (one or
// more chars after the first), which conflates a character check with a length
// check and incorrectly rejects valid 1-char names like `v`. Under permissive
// mode we only check the character set, leaving the length floor to the
// dedicated `isNameTooShort` check above.
const PERMISSIVE_NAME_REGEX = /^[a-zA-Z_][-a-zA-Z0-9_:]*$/;
const hasInvalidCharacters = (
  input: string,
  isPermissiveMode: boolean
): boolean => {
  return isPermissiveMode
    ? !PERMISSIVE_NAME_REGEX.test(input)
    : !isValidAttrName(input);
};
// Compare case-insensitively. Per W3C, attribute names can't contain A–Z, so
// browsers lowercase them on render, making `attr1` and `Attr1` resolve to the same
// DOM attribute and would silently collide. Treat them as the same key here so
// the panel blocks the collision instead of allowing two clashing entries.
export const isNameTaken = (input: string, list: string[]): boolean => {
  const lowered = input.toLowerCase();
  return list.some((name) => name.toLowerCase() === lowered);
};

const isNameReserved = (input: string): boolean => {
  return isInternalAttr(input) || isReservedAttr(input);
};

export const getNameStatus = (
  input: string,
  list: string[],
  isPermissiveMode: boolean
): NameInputStatus => {
  if (!isPermissiveMode && isNameTooShort(input)) return 'invalidLength';
  if (hasInvalidCharacters(input, isPermissiveMode)) return 'invalidCharacters';
  if (isNameTaken(input, list)) return 'taken';
  if (!isPermissiveMode && isNameReserved(input)) return 'reserved';
  return 'valid';
};

const getValueStatus = (input: string): ValueInputStatus => {
  return isValidAttrValue(input) ? 'valid' : 'invalid';
};

type TextOverlay = {text: string; startedFrom: string | null};

const identity = (text: string) => text;

/**
 * The built-in Text editor keeps the input's text in its own state and only
 * re-reads the expression when the expression object changes. To show text the
 * store has not accepted yet -- a pasted half, or a name that failed
 * validation -- hand it a copy of the expression carrying the overlay text, and
 * keep handing it that copy until the store moves.
 */
const useTextOverlay = (
  expr: ContextualExpression | undefined,
  normalizeForStore: (text: string) => string = identity
) => {
  const stored = expr ? ContextualExpressions.getText(stripAnn(expr)) : null;
  const [overlay, setOverlay] = useState<TextOverlay | null>(null);

  // Adjusted during render rather than in an effect: clearing after paint would
  // commit one frame still showing the stale overlay.
  if (
    overlay &&
    (stored === normalizeForStore(overlay.text) ||
      stored !== overlay.startedFrom)
  ) {
    setOverlay(null);
  }

  const displayed = useMemo(
    () =>
      expr && overlay ? updateExpr(() => aEText(overlay.text))(expr) : expr,
    [expr, overlay]
  );

  return {
    stored,
    displayed,
    show: (text: string) => setOverlay({text, startedFrom: stored}),
    track: (text: string) =>
      setOverlay((prev) => (prev ? {...prev, text} : prev)),
    clear: () => setOverlay(null),
  };
};

const RenderCustomAttribute = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1: any) => void;
}) => {
  const [nameStatus, setNameStatus] = useState<NameInputStatus>('valid');
  const [valueStatus, setValueStatus] = useState<ValueInputStatus>('valid');
  const el = useDesignerSelector(selectSelectedElement);
  const siteId = useDesignerSelector(selectSiteId);
  const customControls = React.useContext(CustomControls);
  const onAttributeNameEmptyChange =
    customControls.TopLevelOptionsContext?.onAttributeNameEmptyChange;
  const name = ContextualExpressions.getIn(expr, NAME_PATH);
  const value = ContextualExpressions.getIn(expr, VALUE_PATH);
  const nameField = useTextOverlay(name);
  const valueField = useTextOverlay(value);

  const elData = el ? Expressions.getElementData(el) : undefined;
  const list = elData
    ? (Expressions.getIn(elData, XATTR_PATH) ??
      Expressions.getIn(elData, ATTRIBUTES_PATH))
    : undefined;
  const takenNames = list !== undefined ? getCustomAttributesList(list) : [];

  const exprType = getAnn(expr).type;
  const isPermissiveMode = exprType
    ? getMeta(exprType)?.validationMode === 'permissive'
    : false;

  const extendExpressionEditorForName = (action: any) => {
    return extendExpressionEditorActionAtPath(dispatch)(NAME_PATH)(action);
  };
  const extendExpressionEditorForValue = (action: any) => {
    return extendExpressionEditorActionAtPath(dispatch)(VALUE_PATH)(action);
  };

  if (name && value) {
    const getStatusForName = (candidate: string): NameInputStatus => {
      // do not include the current name in the list of taken names
      const otherNames = takenNames.filter(
        (takenName) => takenName !== nameField.stored
      );
      // the name is checked as typed: SVG attribute names are case-sensitive
      // and forcing lowercase would break them. The case-insensitive taken
      // check inside getNameStatus still blocks collisions on HTML elements,
      // where browsers lowercase names on render.
      return getNameStatus(candidate, otherNames, isPermissiveMode);
    };

    const handleNamePasteCapture = (
      event: React.ClipboardEvent<HTMLDivElement>
    ) => {
      const clipboardText =
        event.clipboardData.getData('text/plain') ||
        event.clipboardData.getData('text');
      const parsed = parseAttributePaste(clipboardText);
      if (!parsed) return;

      // When the parsed name is malformed (bad characters or too short),
      // fall back to the default paste so the raw text lands in the field
      // and the regular input validation surfaces the error next to visible
      // text instead of an empty input.
      const splitNameStatus = getStatusForName(parsed.name);
      if (
        splitNameStatus === 'invalidCharacters' ||
        splitNameStatus === 'invalidLength'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // Reported from here rather than from the dispatches below so the event
      // counts every paste the split intercepts, including the taken/reserved
      // path where only the name half is surfaced.
      trackEvent({
        name: ANALYTICS_EVENTS.CUSTOM_ATTRIBUTE_SPLIT_ON_PASTE,
        data: {
          'site id': siteId,
          'attribute name': parsed.name,
          'attribute value': parsed.value,
        },
      });

      // A taken or reserved name is still split so the Name input's own
      // validation reports the real error ("This attribute already exists." /
      // "This is a reserved name.") instead of the misleading
      // invalid-characters error the raw `name=value` text would trigger.
      // Only the name half is displayed: the invalid name is never committed
      // to the store and the value remains unchanged.
      //
      // A bound value has no editable Text input, so preserve its binding.
      const shouldSplitValue =
        splitNameStatus === 'valid' && valueField.stored !== null;

      // Text ExpressionEditors keep their own focused-input state. Overlay the
      // parsed halves for display while routing persistence through the same
      // validation and dispatch paths used by regular input.
      nameField.show(parsed.name);
      if (shouldSplitValue) valueField.show(parsed.value);

      nameDispatch(updateExpressionAction(EText(parsed.name)));
      if (shouldSplitValue) {
        valueDispatch(updateExpressionAction(EText(parsed.value)));
      }
    };

    const nameDispatch = (action: any) => {
      if (action.type === 'EXPRESSION_ACTION') {
        const newName = Expressions.getText(action.payload.newExpression);
        if (newName !== null) {
          nameField.track(newName);
          onAttributeNameEmptyChange?.(newName === '');

          const status = getStatusForName(newName);
          setNameStatus(status);
          if (status === 'valid') {
            //only update the name field of the custom attribute if the new name is valid
            extendExpressionEditorForName(action);
          }
          return;
        }
      }

      // handle other action types
      nameField.clear();
      onAttributeNameEmptyChange?.(false);
      setNameStatus('valid');
      extendExpressionEditorForName(action);
    };

    const valueDispatch = (action: any) => {
      if (action.type === 'EXPRESSION_ACTION') {
        const newValue = Expressions.getText(action.payload.newExpression);

        if (newValue !== null) {
          valueField.track(newValue);
          const status = getValueStatus(newValue);
          setValueStatus(status);
          if (status === 'valid') extendExpressionEditorForValue(action);
          return;
        }
      }

      valueField.clear();
      setValueStatus('valid');
      extendExpressionEditorForValue(action);
    };

    return (
      <Container>
        <div onPasteCapture={handleNamePasteCapture}>
          <ExpressionEditor
            dispatch={nameDispatch}
            expression={nameField.displayed || expr}
          />
        </div>
        {nameStatus !== 'valid' && (
          <Note
            style={styledNote}
            colorVariant="danger"
            data-automation-id="custom-attribute-name-error-message"
            hasIcon={false}
          >
            <Text>{nameErrorText[nameStatus]}</Text>
          </Note>
        )}
        <ExpressionEditor
          dispatch={valueDispatch}
          expression={valueField.displayed || expr}
        />
        {valueStatus === 'invalid' && (
          <Note
            style={styledNote}
            colorVariant="danger"
            data-automation-id="custom-attribute-value-error-message"
            hasIcon={false}
          >
            <Text>{VALUE_ERROR_MESSAGE}</Text>
          </Note>
        )}
      </Container>
    );
  }
  return <ExpressionEditor expression={expr} dispatch={dispatch} />;
};

type KeySide = 'name' | 'value';

// Tag's internal HStack hardcodes `spacing={1}` (4px) between its prefix icon
// and the label text. A negative right-margin on the icon element composes
// with the flex `gap` to collapse that space without touching Spring internals
// or relying on `data-sc` selectors.
const BoundKeyTag = ({label}: {label: string}) => (
  <Tag
    colorVariant="purple"
    density="compact"
    prefix={<FieldTextIcon colorVariant="inherit" style={{marginRight: -4}} />}
    maxWidth="100%"
  >
    {label}
  </Tag>
);

const PlainKeyText = ({text, side}: {text: string; side: KeySide}) => (
  <SpringText
    truncateToLines={1}
    colorVariant={side === 'name' ? 'secondary' : 'primary'}
  >
    {text}
  </SpringText>
);

const renderAttributeKey = (
  values: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  expr: Expression,
  side: KeySide
): React.ReactNode => {
  const bindingLabel = getBindingLabel(values, expr);
  const content = bindingLabel ? (
    <BoundKeyTag label={bindingLabel} />
  ) : (
    <PlainKeyText text={Expressions.getText(expr) || ''} side={side} />
  );
  return <AttributeKeyCell>{content}</AttributeKeyCell>;
};

const RenderCustomAttributeSummary = ({expr}: {expr: ContextualExpression}) => {
  const [rootCheckerState] = React.useContext(TypeCheckerState);
  const env = getEnvironment(rootCheckerState);

  const rec = Expressions.getRecord(stripAll(expr));
  if (!rec) throw Error('Invalid list item');

  return (
    <HStack
      spacing={0.5}
      align="center"
      minWidth={0}
      maxWidth={ATTRIBUTE_SUMMARY_MAX_WIDTH}
    >
      {rec.name && renderAttributeKey(env.context.values, rec.name, 'name')}
      <span>=</span>
      {rec.value && renderAttributeKey(env.context.values, rec.value, 'value')}
    </HStack>
  );
};

export const CustomAttributeType = TypeImplementation.Alias({
  type: TRecord({
    name: withSystem({isBindable: false, private: false, label: 'Name'}, TText),
    value: withSystem(
      {isBindable: true, private: false, label: 'Value'},
      TText
    ),
  }),
  render: RenderCustomAttribute,
  renderSummary: RenderCustomAttributeSummary,
});
